import {
  Button,
  ConfirmDialog,
  Grid,
  Checkbox,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Slider,
  TextField,
} from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { TunaPluginRenderPropertiesProps } from '@tunasong/plugin-lib'
import type { Poll, PollResponse } from '@tunasong/schemas'
import { isDatePoll, isDateSlot, isManyOfManyPoll, isPoll, pollTypes } from '@tunasong/schemas'
import { EntitySelect } from '@tunasong/tunasong-ui'
import type { ChangeEventHandler, FC } from 'react'
import React from 'react'
import { useCallback } from 'react'
import { getAnswerCounts } from './availability.js'
import { formatSlotLabel } from './date-slot.js'
import { SlotListEditor } from './slot-list-editor.js'
import css from './properties.module.css'

export interface PropertiesProps extends TunaPluginRenderPropertiesProps<Poll> {
  className?: string
}

export const Properties: FC<PropertiesProps> = props => {
  const { className, element, onChange } = props

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } = graphHooks.useEntityChildren<PollResponse>({
    parentId: element.id,
    filter: 'pollresponse',
  })

  const handleClick = useCallback(() => {
    if (!isPoll(element)) {
      return
    }
    const { pollOptions = [] } = element
    const updated = {
      ...element,
      pollOptions: [...pollOptions, { name: '' }],
    }
    onChange(updated)
  }, [element, onChange])

  const updateOption = useCallback(
    (idx: number): ChangeEventHandler<HTMLInputElement> =>
      ({ target: { value: name } }) => {
        if (!isPoll(element)) {
          return
        }
        const pollOptions = [...element.pollOptions]
        /** Spread the existing option: `{ name }` alone dropped entityRef, start, end and removed. */
        pollOptions[idx] = { ...pollOptions[idx], name }
        const updated = { ...element, pollOptions }
        onChange(updated)
      },
    [element, onChange]
  )

  const updateOptionRef = (idx: number, entityRef?: string) => {
    const pollOptions = [...element.pollOptions]
    pollOptions[idx] = { ...pollOptions[idx], entityRef }
    const updated = { ...element, pollOptions }
    onChange(updated)
  }

  /**
   * Never splice. Responses are keyed by option index, so removing element 1 of [0,1,2]
   * re-points every stored answer for element 2 onto element 1 — silently, and the symptom
   * is someone turning up on the wrong night. The option stays as a tombstone and is
   * filtered out of every render.
   *
   * And never un-set `removed`: re-adding the same option appends a NEW index, so old
   * answers cannot reattach to a question that was asked again.
   */
  const tombstone = useCallback(
    (idx: number) => {
      if (!isPoll(element)) {
        return
      }
      const pollOptions = element.pollOptions.map((option, i) => (i === idx ? { ...option, removed: true } : option))
      onChange({ ...element, pollOptions })
    },
    [element, onChange]
  )

  /**
   * Renaming and removing are different risks. Renaming silently changes what people agreed
   * to, so it stays blocked. Removing is visible and discards cleanly, so it is allowed —
   * behind a confirm when anyone has answered, and immediately when nobody has. The common
   * case should not pay for the rare one.
   */
  const [pendingRemoval, setPendingRemoval] = React.useState<number | undefined>(undefined)

  const handleCancelRemoval = () => setPendingRemoval(undefined)
  const handleConfirmRemoval = () => {
    if (typeof pendingRemoval === 'number') {
      tombstone(pendingRemoval)
    }
    setPendingRemoval(undefined)
  }

  const handleChangeType = useCallback(
    (ev: unknown, pollType: string) => {
      const updated = { ...element, pollType: pollType as never } satisfies Poll
      onChange(updated)
    },
    [element, onChange]
  )

  const handleQuotaChange = useCallback(
    (ev: unknown, value: number | number[]) => {
      const responseQuota = typeof value === 'number' && value > 0 ? value : undefined
      const updated = { ...element, responseQuota }
      onChange(updated)
    },
    [element, onChange]
  )

  const handleRandomize = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...element, randomizeOptions: ev.target.checked }
    onChange(updated)
  }

  if (!isPoll(element) || !hasLoadedChildren) {
    return null
  }
  const { pollOptions = [], pollType } = element

  /**
   * The panel used to be replaced wholesale by "Cannot edit poll with responses". That was a
   * crude way to make the splice above unreachable; now that removal is a tombstone, only the
   * options somebody has actually answered need freezing. Adding one is always safe, because
   * appending shifts no index.
   */
  const answerCounts = getAnswerCounts(allResponses)
  const answerCount = (idx: number) => answerCounts.get(idx) ?? 0
  const hasAnyAnswers = answerCounts.size > 0

  const optionLabel = (idx: number) => {
    const option = pollOptions[idx]
    return option && isDateSlot(option) ? formatSlotLabel(option.start) : (option?.name ?? '')
  }

  const removalCount = typeof pendingRemoval === 'number' ? answerCount(pendingRemoval) : 0
  const removalText =
    removalCount === 1
      ? `1 person has already answered for ${optionLabel(pendingRemoval ?? 0)}. Their answers for this date will be discarded.`
      : `${removalCount} people have already answered for ${optionLabel(pendingRemoval ?? 0)}. Their answers for this date will be discarded.`

  /** A rehearsal poll authors slots, not text options. */
  if (isDatePoll(element)) {
    return <SlotListEditor element={element} answerCounts={answerCounts} onChange={onChange} />
  }

  const handleRemoveClick = (idx: number) => () => {
    if (answerCount(idx) > 0) {
      setPendingRemoval(idx)
      return
    }
    tombstone(idx)
  }

  return (
    <div className={[css.root, className].filter(Boolean).join(' ')}>
      <FormControl>
        <RadioGroup aria-label="options" name="options" value={pollType ?? ''} onChange={handleChangeType}>
          {pollTypes.map(value => (
            <FormControlLabel key={value} value={value} control={<Radio />} label={value} />
          ))}
        </RadioGroup>
      </FormControl>

      {isManyOfManyPoll(element) && (
        <>
          <FormLabel className={css.formLabel}>Vote quota</FormLabel>
          <div className={css.sliderPadding}>
            <Slider
              value={typeof element.responseQuota === 'number' ? element.responseQuota : 0}
              onChange={handleQuotaChange}
              min={0}
              max={element.pollOptions?.length}
            />
          </div>
        </>
      )}

      <FormLabel className={css.formLabel}>Options</FormLabel>
      <FormControlLabel
        control={<Checkbox checked={element?.randomizeOptions} onChange={handleRandomize} />}
        label="Randomize order of options in poll"
      />
      <Typography variant="caption" className={css.helpText}>
        Add options for users to vote on. You can give the option a name, or optionally link to an entity that
        represents the option (e.g., a song or audio clip)
      </Typography>

      {pollOptions.map((option, idx) =>
        option.removed ? null : (
          <Grid container key={idx}>
            <Grid size={{ xs: 3 }}>
              <EntitySelect
                buttonText="Link Entity "
                entityId={option.entityRef}
                onSelected={entityRef => updateOptionRef(idx, entityRef)}
              />
            </Grid>
            <Grid size={{ xs: 4 }} className={css.optionGridFlex}>
              {option.entityRef ? (
                <Typography variant="caption" color="primary">
                  Linked
                </Typography>
              ) : (
                <TextField
                  className={css.optionTextField}
                  placeholder="Option"
                  defaultValue={option.name}
                  onChange={updateOption(idx)}
                  variant="standard"
                  disabled={Boolean(option.entityRef) || answerCount(idx) > 0}
                />
              )}
            </Grid>
            <Grid size={{ xs: 2 }}>
              {option.entityRef && answerCount(idx) === 0 ? (
                <Button onClick={() => updateOptionRef(idx, undefined)}>Clear Ref</Button>
              ) : null}
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Button onClick={handleRemoveClick(idx)}>Delete</Button>
            </Grid>
            {answerCount(idx) > 0 ? (
              <Typography variant="caption" color="textSecondary" className={css.helpText}>
                {answerCount(idx) === 1 ? '1 answer' : `${answerCount(idx)} answers`}
              </Typography>
            ) : null}
          </Grid>
        )
      )}
      {hasAnyAnswers ? (
        <Typography variant="caption" color="textSecondary" className={css.helpText}>
          Dates people have already answered can&apos;t be renamed — remove and re-add instead.
        </Typography>
      ) : null}

      <Button onClick={handleClick} className={css.createButton}>
        Create option
      </Button>

      <ConfirmDialog
        open={typeof pendingRemoval === 'number'}
        title="Remove this date?"
        text={removalText}
        okText="Remove date"
        onCancel={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  )
}

export default Properties
